"use client";

/**
 * App Providers
 *
 * Client-side wrapper for app-level providers and error boundaries.
 * SocketBridge is a renderless component — no wrapper nesting needed.
 *
 * Provider order (innermost to outermost):
 * - ThemeProvider (no deps)
 * - SocketBridge (needs AuthProvider for auth token — renders null)
 * - AuthProvider (no deps)
 * - QueryClientProvider (React Query cache for server data)
 * - ErrorBoundary (wraps all)
 */

import { type ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundary } from "./ErrorBoundary";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { SocketBridge } from "./SocketBridge";
import { PostHogProvider } from "./PostHogProvider";
import { ToastProvider } from "@/components/ui/Toast";
import { getQueryClient } from "@/lib/queryClient";

interface AppProvidersProps {
  children: ReactNode;
}

export function AppProviders({ children }: AppProvidersProps): ReactNode {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={getQueryClient()}>
        <AuthProvider>
          <SocketBridge />
          <PostHogProvider>
            <ThemeProvider>
              <ToastProvider>{children}</ToastProvider>
            </ThemeProvider>
          </PostHogProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
